import React, { useCallback, useEffect } from 'react'
import { Badge, Banner, Button } from '../../../components'
import formStyles from '../../../styles/forms.module.scss'
import utilStyles from '../../../styles/utilities.module.scss'
import styles from './GoogleAdSlotEditor.module.scss'

export interface GoogleAdSlotMetadata {
  ad_unit_path?: string
  sizes_desktop?: number[][]
  sizes_mobile?: number[][]
}

interface GoogleAdSlotEditorProps {
  metadata: GoogleAdSlotMetadata
  onChange: (metadata: GoogleAdSlotMetadata) => void
}

// Pre-fill values shown for new widgets. Once saved, the widget keeps
// whatever sizes the merchandiser confirmed — bumping these later won't
// retroactively change existing widgets.
const DEFAULT_SIZES_DESKTOP: number[][] = [[728, 90], [970, 90]]
const DEFAULT_SIZES_MOBILE:  number[][] = [[320, 50]]

const GoogleAdSlotEditor: React.FC<GoogleAdSlotEditorProps> = ({ metadata, onChange }) => {
  const update = useCallback(
    (patch: Partial<GoogleAdSlotMetadata>) => onChange({ ...metadata, ...patch }),
    [metadata, onChange],
  )

  // Seed defaults on first mount for a fresh widget so a merchandiser who
  // doesn't touch the size rows still saves a complete config. Editing an
  // existing widget leaves stored values alone (including intentionally
  // empty lists).
  useEffect(() => {
    const patch: Partial<GoogleAdSlotMetadata> = {}
    if (metadata.sizes_desktop === undefined) patch.sizes_desktop = DEFAULT_SIZES_DESKTOP
    if (metadata.sizes_mobile  === undefined) patch.sizes_mobile  = DEFAULT_SIZES_MOBILE
    if (Object.keys(patch).length > 0) onChange({ ...metadata, ...patch })
    // Mount-only — subsequent renders use whatever the merchandiser set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sizesDesktop = metadata.sizes_desktop ?? DEFAULT_SIZES_DESKTOP
  const sizesMobile  = metadata.sizes_mobile  ?? DEFAULT_SIZES_MOBILE

  return (
    <>
      <Banner variant="info" message="Ads are disabled in the mobile app — this widget renders only on the web." />

      <div className={formStyles.fieldGroup}>
        <label>Ad Unit <Badge variant="neutral" size="small">Internal</Badge></label>
        <input
          type="text"
          value={metadata.ad_unit_path || ''}
          onChange={(e) => update({ ad_unit_path: e.target.value })}
          placeholder="/4756/TCOM/Scores"
        />
        <p className={formStyles.hint}>
          GAM ad unit path from the ads team. Format: <code>/network/section[/subsection]</code>.
        </p>
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Desktop Sizes <Badge variant="neutral" size="small">Internal</Badge></label>
        <SizeList sizes={sizesDesktop} onChange={(s) => update({ sizes_desktop: s })} />
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Mobile Sizes <Badge variant="neutral" size="small">Internal</Badge></label>
        <SizeList sizes={sizesMobile} onChange={(s) => update({ sizes_mobile: s })} />
      </div>
    </>
  )
}

interface SizeListProps {
  sizes: number[][]
  onChange: (sizes: number[][]) => void
}

const SizeList: React.FC<SizeListProps> = ({ sizes, onChange }) => {
  const updateCell = (idx: number, col: 0 | 1, raw: string) => {
    const parsed = raw === '' ? 0 : Math.max(0, parseInt(raw, 10) || 0)
    onChange(
      sizes.map((s, i) => (i === idx ? (col === 0 ? [parsed, s[1]] : [s[0], parsed]) : s)),
    )
  }
  const addRow    = () => onChange([...sizes, [0, 0]])
  const removeRow = (idx: number) => onChange(sizes.filter((_, i) => i !== idx))

  return (
    <div className={styles.sizeList}>
      {sizes.map((size, idx) => (
        <div key={idx} className={styles.sizeRow}>
          <input
            type="number"
            min={1}
            value={size[0] || ''}
            onChange={(e) => updateCell(idx, 0, e.target.value)}
            className={styles.sizeInput}
            placeholder="W"
          />
          <span>×</span>
          <input
            type="number"
            min={1}
            value={size[1] || ''}
            onChange={(e) => updateCell(idx, 1, e.target.value)}
            className={styles.sizeInput}
            placeholder="H"
          />
          <button
            type="button"
            className={`${utilStyles.iconBtn} ${utilStyles.iconBtnDanger}`}
            onClick={() => removeRow(idx)}
            aria-label="Remove size"
            title="Remove size"
          >
            ×
          </button>
        </div>
      ))}
      <Button type="button" variant="secondary" size="small" onClick={addRow}>
        + Add size
      </Button>
    </div>
  )
}

export default GoogleAdSlotEditor
